import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type OnboardingMilestone =
  | 'agentConfigured'
  | 'personaConfigured'
  | 'evaluatorConfigured'
  | 'simulationRun'
  | 'evaluationOpened'
  | 'evaluationsRun'
  | 'evaluationScoreOpened'
  | 'improvementSuggestionsRequested'
  | 'evaluationExported';

export type OnboardingTourKey =
  | 'agent-builder'
  | 'persona-builder'
  | 'evaluator-builder'
  | 'conversation-simulation'
  | 'conversation-result'
  | 'evaluations'
  | 'evaluation-results'
  | 'evaluation-iteration'
  | 'closing'
  | 'settings'
  | 'connections';

export type OnboardingTransitionKey =
  | 'agent-to-persona'
  | 'persona-to-evaluator'
  | 'evaluator-to-simulation'
  | 'simulation-to-evaluations'
  | 'evaluations-to-settings'
  | 'settings-to-connections';

interface TutorialState {
  hasSeenIntro: boolean;
  isDismissed: boolean;
  isActive: boolean;
  showResumePrompt: boolean;
  showCompletionPrompt: boolean;
  hasSeenSimulationWaitTip: boolean;
  completedMilestones: Partial<Record<OnboardingMilestone, boolean>>;
  completedTours: Partial<Record<OnboardingTourKey, boolean>>;
  completedTransitions: Partial<Record<OnboardingTransitionKey, boolean>>;
  /** The simulation the guide ran, so resuming can reopen it. */
  onboardingSimulationId: string | null;
  startOnboarding: () => void;
  restartOnboarding: () => void;
  resumeOnboarding: () => void;
  skipOnboarding: () => void;
  dismissResumePrompt: () => void;
  completeOnboarding: () => void;
  showOnboardingCompletionPrompt: () => void;
  markSimulationWaitTipSeen: () => void;
  markMilestoneComplete: (milestone: OnboardingMilestone) => void;
  markTourComplete: (tour: OnboardingTourKey) => void;
  markTransitionComplete: (transition: OnboardingTransitionKey) => void;
  setOnboardingSimulation: (simulationId: string | null) => void;
  resetOnboarding: () => void;
}

type PersistedTutorialState = Partial<
  Pick<
    TutorialState,
    | 'hasSeenIntro'
    | 'isDismissed'
    | 'isActive'
    | 'showResumePrompt'
    | 'showCompletionPrompt'
    | 'hasSeenSimulationWaitTip'
    | 'completedMilestones'
    | 'completedTours'
    | 'completedTransitions'
  >
> & {
  activeFlow?: unknown;
  hasSeenTutorial?: unknown;
};

const initialState: Pick<
  TutorialState,
  | 'hasSeenIntro'
  | 'isDismissed'
  | 'isActive'
  | 'showResumePrompt'
  | 'showCompletionPrompt'
  | 'hasSeenSimulationWaitTip'
  | 'completedMilestones'
  | 'completedTours'
  | 'completedTransitions'
  | 'onboardingSimulationId'
> = {
  hasSeenIntro: false,
  isDismissed: false,
  isActive: false,
  showResumePrompt: false,
  showCompletionPrompt: false,
  hasSeenSimulationWaitTip: false,
  completedMilestones: {},
  completedTours: {},
  completedTransitions: {},
  onboardingSimulationId: null,
};

export const useTutorialStore = create<TutorialState>()(
  persist(
    (set) => ({
      ...initialState,
      startOnboarding: () =>
        set({
          hasSeenIntro: true,
          isDismissed: false,
          isActive: true,
          showResumePrompt: false,
          showCompletionPrompt: false,
          hasSeenSimulationWaitTip: false,
          completedMilestones: {},
          completedTours: {},
          completedTransitions: {},
          onboardingSimulationId: null,
        }),
      restartOnboarding: () =>
        set({
          hasSeenIntro: false,
          isDismissed: false,
          isActive: false,
          showResumePrompt: false,
          showCompletionPrompt: false,
          hasSeenSimulationWaitTip: false,
          completedMilestones: {},
          completedTours: {},
          completedTransitions: {},
          onboardingSimulationId: null,
        }),
      resumeOnboarding: () =>
        set({
          hasSeenIntro: true,
          isDismissed: false,
          isActive: true,
          showResumePrompt: false,
          showCompletionPrompt: false,
        }),
      skipOnboarding: () =>
        set({
          hasSeenIntro: true,
          isDismissed: true,
          isActive: false,
          showResumePrompt: true,
          showCompletionPrompt: false,
        }),
      dismissResumePrompt: () =>
        set({
          showResumePrompt: false,
        }),
      completeOnboarding: () =>
        set({
          hasSeenIntro: true,
          isDismissed: true,
          isActive: false,
          showResumePrompt: false,
          showCompletionPrompt: false,
        }),
      showOnboardingCompletionPrompt: () =>
        set({
          showCompletionPrompt: true,
          showResumePrompt: false,
        }),
      markSimulationWaitTipSeen: () =>
        set({
          hasSeenSimulationWaitTip: true,
        }),
      markMilestoneComplete: (milestone) =>
        set((state) => ({
          completedMilestones: {
            ...state.completedMilestones,
            [milestone]: true,
          },
        })),
      markTourComplete: (tour) =>
        set((state) => ({
          completedTours: {
            ...state.completedTours,
            [tour]: true,
          },
        })),
      markTransitionComplete: (transition) =>
        set((state) => ({
          completedTransitions: {
            ...state.completedTransitions,
            [transition]: true,
          },
        })),
      setOnboardingSimulation: (simulationId) =>
        set({ onboardingSimulationId: simulationId }),
      resetOnboarding: () => set(initialState),
    }),
    {
      name: 'tutorial-storage',
      version: 5,
      migrate: (persistedState) => {
        const persisted =
          persistedState && typeof persistedState === 'object'
            ? (persistedState as PersistedTutorialState)
            : {};
        const legacyHasSeenTutorial =
          typeof persisted.hasSeenTutorial === 'boolean'
            ? persisted.hasSeenTutorial
            : undefined;

        return {
          ...initialState,
          hasSeenIntro:
            persisted.hasSeenIntro ??
            legacyHasSeenTutorial ??
            initialState.hasSeenIntro,
          isDismissed: persisted.isDismissed ?? initialState.isDismissed,
          showResumePrompt:
            persisted.showResumePrompt ?? initialState.showResumePrompt,
          showCompletionPrompt:
            persisted.showCompletionPrompt ?? initialState.showCompletionPrompt,
          hasSeenSimulationWaitTip:
            persisted.hasSeenSimulationWaitTip ??
            initialState.hasSeenSimulationWaitTip,
          isActive:
            persisted.isActive ??
            // Preserve existing guided-build sessions, but do not keep the
            // removed example fast path active after migration.
            persisted.activeFlow === 'build',
          completedMilestones:
            persisted.completedMilestones ?? initialState.completedMilestones,
          completedTours:
            persisted.completedTours ?? initialState.completedTours,
          completedTransitions:
            persisted.completedTransitions ?? initialState.completedTransitions,
        };
      },
    },
  ),
);
